import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Splash from './components/Splash';
import { isStandalone } from './utils/pushPlatform';
import { BrandingProvider } from './contexts/BrandingContext';
import FeatureRoute from './components/FeatureRoute';
import SuperAdminLayout from './components/SuperAdminLayout';
import AdminLogin from './pages/AdminLogin';
import { isAdminSite, isPlatformSite, siteName, setFavicon } from './utils/adminSite';
import { RateLimitProvider, useRateLimit } from './contexts/RateLimitContext';
import { setRateLimitContext } from './utils/apiInterceptor';
import TrackingRoute from './components/TrackingRoute';
import AccessRoute from './components/AccessRoute';
import AppLayout from './components/AppLayout';
import RateLimitBanner from './components/RateLimitBanner';
import Login from './pages/Login';
import ChildLogin from './pages/ChildLogin';
import OpenClub from './pages/OpenClub';
import ChildLayout from './pages/child/ChildLayout';
import PreviewAs from './pages/PreviewAs';
import ChildHome from './pages/child/ChildHome';
import ChildLevel from './pages/child/ChildLevel';
import ChildCertificates from './pages/child/ChildCertificates';
import ChildRoutines from './pages/child/ChildRoutines';
import Register from './pages/Register';
import StartClub from './pages/StartClub';
import Join from './pages/Join';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyClubEmail from './pages/VerifyClubEmail';
import Dashboard from './pages/Dashboard';
import Gymnasts from './pages/Gymnasts';
import Levels from './pages/Levels';
import Skills from './pages/Skills';
import RoutinesToCheck from './pages/RoutinesToCheck';
import StaffDocuments from './pages/StaffDocuments';
import StaffDocument from './pages/StaffDocument';
import WelfareOutcomes from './pages/WelfareOutcomes';
import { isStaffPath, mustSignStaffDocuments } from './utils/staffDocuments';
import Progress from './pages/Progress';
import DiaryOverview from './pages/DiaryOverview';
import MyProgress from './pages/MyProgress';
import MyDiary from './pages/MyDiary';
import Certificates from './pages/Certificates';
import CertificateDesigner from './pages/CertificateDesigner';
import CertificatePreview from './pages/CertificatePreview';
import ClubSettings from './pages/ClubSettings';
import Branding from './pages/Branding';
import Health from './pages/Health';
import Cheatsheets from './pages/Cheatsheets';
import CheatsheetViewer from './pages/CheatsheetViewer';
import WaveLength from './pages/WaveLength';
import BookingCalendar from './pages/booking/BookingCalendar';
import CartConfirmation from './pages/booking/CartConfirmation';
import SessionDetail from './pages/booking/SessionDetail';
import BookingConfirmation from './pages/booking/BookingConfirmation';
import MyBookings from './pages/booking/MyBookings';
import BookingAdmin from './pages/booking/admin/BookingAdmin';
import AdminClosures from './pages/booking/admin/AdminClosures';
import AdminMembers from './pages/booking/admin/AdminMembers';
import AdminMemberProfile from './pages/booking/admin/AdminMemberProfile';
import AdminBgNumbers from './pages/booking/admin/AdminBgNumbers';
import ShopConfirmation from './pages/booking/shop/ShopConfirmation';
import MyOrders from './pages/booking/shop/MyOrders';
import AdminShopOrders from './pages/booking/admin/AdminShopOrders';
import AdminShopProducts from './pages/booking/admin/AdminShopProducts';
import AdminShopProduct from './pages/booking/admin/AdminShopProduct';
import AdminCardPayments from './pages/booking/admin/AdminCardPayments';
import AdminRecipientGroups from './pages/booking/admin/AdminRecipientGroups';
import AdminCharges from './pages/booking/admin/AdminCharges';
import AdminInvoices from './pages/booking/admin/AdminInvoices';
import AdminInvoiceDetail from './pages/booking/admin/AdminInvoiceDetail';
import AdminBusinessContacts from './pages/booking/admin/AdminBusinessContacts';
import AdminCredits from './pages/booking/admin/AdminCredits';
import AdminMemberships from './pages/booking/admin/AdminMemberships';
import AdminMemberPayments from './pages/booking/admin/AdminMemberPayments';
import AdminRegister from './pages/booking/admin/AdminRegister';
import NextRegister from './pages/booking/admin/NextRegister';
import MyCompetitions from './pages/booking/MyCompetitions';
import AdminDashboard from './pages/AdminDashboard';
import ScrollToTop from './components/ScrollToTop';
import AdminIncidents from './pages/booking/admin/AdminIncidents';
import AdminIncidentDetail from './pages/booking/admin/AdminIncidentDetail';
import AdminIncidentForm from './pages/booking/admin/AdminIncidentForm';
import MyIncidents from './pages/booking/MyIncidents';
import { MyIncidentDetail } from './pages/booking/MyIncidents';
import AdminWelfare from './pages/booking/admin/AdminWelfare';
import AdminWelfareDetail from './pages/booking/admin/AdminWelfareDetail';
import AdminWelfareForm from './pages/booking/admin/AdminWelfareForm';
import AcceptInvite from './pages/booking/AcceptInvite';
import PublicHome from './pages/public/PublicHome';
import './App.css';
// Shared styles for every page, after App.css as when each page loaded them
import './pages/booking/booking-shared.css';

// Pages that are rarely used, or pull in big libraries (Stripe, the rich text
// editor), load on first visit so the app itself downloads and starts faster.
const SessionManagement = lazy(() => import('./pages/booking/admin/SessionTemplates'));
const AdminCoachHours = lazy(() => import('./pages/booking/admin/AdminCoachHours'));
const AdminSessionTypes = lazy(() => import('./pages/booking/admin/AdminSessionTypes'));
const AdminRoles = lazy(() => import('./pages/booking/admin/AdminRoles'));
const Noticeboard = lazy(() => import('./pages/booking/Noticeboard'));
const Messages = lazy(() => import('./pages/booking/Messages'));
const Conversation = lazy(() => import('./pages/booking/Conversation'));
const AdminMessages = lazy(() => import('./pages/booking/admin/AdminMessages'));
const AdminChat = lazy(() => import('./pages/booking/admin/AdminChat'));
const ChatGroupForm = lazy(() => import('./pages/booking/admin/ChatGroupForm'));
const Cart = lazy(() => import('./pages/booking/Cart'));
const Checkout = lazy(() => import('./pages/booking/Checkout'));
const CompetitionEntry = lazy(() => import('./pages/booking/CompetitionEntry'));
const MyChildren = lazy(() => import('./pages/booking/MyChildren'));
const NotificationsPage = lazy(() => import('./pages/booking/NotificationsPage'));
const SuperAdmin = lazy(() => import('./pages/SuperAdmin'));
const AdminChangelog = lazy(() => import('./pages/booking/admin/AdminChangelog'));
const AuditLog = lazy(() => import('./pages/booking/admin/AuditLog'));
const AdminHelpPage = lazy(() => import('./pages/booking/admin/AdminHelpPage'));
const HelpPage = lazy(() => import('./pages/booking/HelpPage'));
const AdminCompetitionDetail = lazy(() => import('./pages/booking/admin/AdminCompetitionDetail'));
const AdminCompetitions = lazy(() => import('./pages/booking/admin/AdminCompetitions'));
const Requirements = lazy(() => import('./pages/booking/Requirements'));
const AdminPaymentsPage = lazy(() => import('./pages/booking/admin/AdminPaymentsPage'));
const PayInvoice = lazy(() => import('./pages/public/PayInvoice'));
const PublicPolicies = lazy(() => import('./pages/public/PublicPolicies'));
const ShopListing = lazy(() => import('./pages/booking/shop/ShopListing'));
const CommunityShop = lazy(() => import('./pages/booking/community/CommunityShop'));
const AdminCommunityShop = lazy(() => import('./pages/booking/admin/AdminCommunityShop'));
const CommunityListing = lazy(() => import('./pages/booking/community/CommunityListing'));
const CommunityListingForm = lazy(() => import('./pages/booking/community/CommunityListingForm'));
const ShopProduct = lazy(() => import('./pages/booking/shop/ShopProduct'));

// The old Payouts address, kept for links and Stripe returns already out there
function PayoutsRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/booking/admin/card-payments${search}`} replace />;
}

function PageMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const name = siteName();
    if (pathname.startsWith('/cheatsheets')) {
      document.title = '2026 BG Rules Cheatsheets - Trampoline & DMT | British Gymnastics';
      setFavicon('/favicon.png');
    } else if (pathname.startsWith('/booking')) {
      document.title = `Booking | ${name}`;
      setFavicon('/tl-favicon.png');
    } else if (
      pathname === '/' ||
      pathname.startsWith('/gymnasts') ||
      pathname.startsWith('/levels') ||
      pathname.startsWith('/competitions') ||
      pathname.startsWith('/progress') ||
      pathname.startsWith('/my-progress') ||
      pathname.startsWith('/my-diary') ||
      pathname.startsWith('/diary-overview') ||
      pathname.startsWith('/certificates') ||
      pathname.startsWith('/profile') ||
      pathname.startsWith('/club-settings') ||
      pathname.startsWith('/branding')
    ) {
      document.title = `Tracker | ${name}`;
      setFavicon('/tl-favicon.png');
    } else {
      document.title = name;
      setFavicon('/tl-favicon.png');
    }
  }, [pathname]);

  return null;
}

// Inner component to access rate limit context
// Gymnasts signed in with a code get their own simple pages, never the main app.
function MainLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user?.role === 'CHILD') return <Navigate to="/child" replace />;
  if (user?.role === 'SUPER_ADMIN') return <Navigate to="/super-admin" replace />;
  // A club that requires staff documents keeps staff who haven't signed them out of staff pages
  if (mustSignStaffDocuments(user) && isStaffPath(pathname)) return <Navigate to="/staff-documents" replace />;
  return <AppLayout />;
}

// The Home Screen app opens at "/": someone signed in goes to their
// dashboard (the splash until we know), not the club's public home page
function HomeRoute() {
  const { isAuthenticated, loading } = useAuth();
  let hasToken = false;
  try { hasToken = !!localStorage.getItem('token'); } catch { /* storage blocked */ }
  if (!isStandalone() || !hasToken) return <PublicHome />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  if (loading) return <Splash />;
  return <PublicHome />;
}

// Until the first page has shown, loading a page's code keeps the splash up;
// after that, moving between pages shows the usual spinner
let firstPageShown = false;
function MarkFirstPageShown() {
  const { loading } = useAuth();
  useEffect(() => { if (!loading) firstPageShown = true; }, [loading]);
  return null;
}
const PageFallback = () => (firstPageShown ? <div className="loading"><div className="spinner" /></div> : <Splash />);

function AppContent() {
  const rateLimitContext = useRateLimit();

  useEffect(() => {
    // Set up the API interceptor with rate limit context
    setRateLimitContext(rateLimitContext);

    // Add body class when rate limited for layout adjustments
    const updateBodyClass = () => {
      if (rateLimitContext.isRateLimited) {
        document.body.classList.add('rate-limited');
      } else {
        document.body.classList.remove('rate-limited');
      }
    };

    updateBodyClass();

    // In development mode, expose rate limit testing function to console
    if (process.env.NODE_ENV === 'development') {
      window.testRateLimit = (seconds = 10) => {
        console.log('🧪 Testing rate limit banner...');
        rateLimitContext.triggerRateLimitForTesting(seconds);
      };
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('rate-limited');
      if (process.env.NODE_ENV === 'development') {
        delete window.testRateLimit;
      }
    };
  }, [rateLimitContext]);

  // admin.runthe.club only has the super admin portal
  if (isAdminSite()) setFavicon('/favicon-root.png');
  if (isAdminSite()) {
    return (
      <div className="App">
        <ScrollToTop />
        <RateLimitBanner />
        <Suspense fallback={<div className="loading"><div className="spinner" /></div>}>
          <Routes>
            <Route path="/login" element={<AdminLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-club-email" element={<VerifyClubEmail />} />
            <Route element={<SuperAdminLayout />}>
              <Route path="/super-admin" element={<SuperAdmin />} />
            </Route>
            <Route path="*" element={<Navigate to="/super-admin" replace />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  return (
    <div className="App">
      <ScrollToTop />
      <PageMeta />
      <RateLimitBanner />
      <Suspense fallback={<PageFallback />}>
      <MarkFirstPageShown />
      <Routes>
        {/* Public routes */}
        {/* app.runthe.club is only for signing in; each club's own site has its home page and sign-up */}
        <Route path="/" element={isPlatformSite() ? <Navigate to="/login" replace /> : <HomeRoute />} />
        <Route path="/policies" element={<PublicPolicies />} />
        <Route path="/pay/invoice/:token" element={<PayInvoice />} />
        <Route path="/login" element={<Login />} />
        <Route path="/child-login" element={<ChildLogin />} />
        <Route path="/open-club" element={<OpenClub />} />
        {/* A club signing itself up; works on app.runthe.club too */}
        <Route path="/start" element={<StartClub />} />
        <Route path="/register" element={isPlatformSite() ? <Navigate to="/login" replace /> : <Register />} />
        {/* A club's own sign-up link works on every club-facing site */}
        <Route path="/join/:name" element={<Join />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-club-email" element={<VerifyClubEmail />} />
        <Route path="/cheatsheets" element={<Cheatsheets />} />
        <Route path="/cheatsheets/:cheatsheetId" element={<CheatsheetViewer />} />
        <Route path="/wavelength" element={<WaveLength />} />

        {/* All authenticated routes under AppLayout */}
        <Route path="/child" element={<FeatureRoute feature="gymnastApp" to="/login" />}>
        <Route element={<ChildLayout />}>
          <Route index element={<ChildHome />} />
          <Route path="level" element={<ChildLevel />} />
          <Route element={<FeatureRoute feature="certificates" to="/child" />}><Route path="certificates" element={<ChildCertificates />} /></Route>
          <Route element={<FeatureRoute feature="diary" to="/child" />}><Route path="diary" element={<MyDiary />} /></Route>
          <Route path="routines" element={<ChildRoutines />} />
          {/* 16 and 17-year-olds signed in with a parent's code, in groups that allow them */}
          <Route element={<FeatureRoute feature="messaging" to="/child" />}>
            <Route path="messages" element={<Messages />} />
            <Route path="messages/:id" element={<Conversation />} />
          </Route>
        </Route>
        </Route>

        <Route element={<SuperAdminLayout />}>
          <Route path="/super-admin" element={<SuperAdmin />} />
        </Route>

        <Route element={<MainLayout />}>
          {/* Universal — all authenticated roles */}
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="preview-as" element={<PreviewAs />} />
          <Route path="profile" element={<Navigate to="/booking/my-account" replace />} />
          <Route path="my-progress" element={<MyProgress />} />
          <Route element={<FeatureRoute feature="diary" />}><Route path="my-diary" element={<MyDiary />} /></Route>
          {/* Certificates are on Progress; the old page and links land there */}
          <Route path="my-certificates" element={<Navigate to="/my-progress" replace />} />
          <Route path="health" element={<Health />} />
          <Route element={<FeatureRoute feature="codeOfConduct" to="/dashboard" />}>
            <Route path="staff-documents" element={<StaffDocuments />} />
            <Route path="staff-documents/:id" element={<StaffDocument />} />
          </Route>
          <Route path="code-of-conduct" element={<Navigate to="/staff-documents" replace />} />
          <Route path="welfare-outcomes" element={<WelfareOutcomes />} />

          {/* Staff-only tracking routes, each for staff with access to its area */}
          <Route element={<TrackingRoute />}>
            <Route element={<AccessRoute staff />}><Route path="admin-hub" element={<AdminDashboard />} /></Route>
            <Route element={<AccessRoute area="skillTracking" level="VIEW" />}>
              <Route path="gymnasts" element={<Gymnasts />} />
              {/* Everyone with skill tracking reads the skills library; changes need skill setup */}
              <Route path="skills" element={<Skills />} />
              <Route element={<FeatureRoute feature="gymnastRoutines" />}><Route path="routines-to-check" element={<RoutinesToCheck />} /></Route>
              <Route path="progress/:gymnastId" element={<Progress />} />
              <Route element={<FeatureRoute feature="diary" />}><Route path="diary-overview" element={<DiaryOverview />} /></Route>
            </Route>
            {/* Competition Categories used to live here; competitions are under Admin now */}
            <Route path="competitions" element={<Navigate to="/booking/admin/competitions" replace />} />
            <Route element={<AccessRoute area="skillSetup" level="MANAGE" />}>
              <Route path="levels" element={<Levels />} />
              <Route element={<FeatureRoute feature="certificates" />}><Route path="certificates" element={<Certificates />} /></Route>
              <Route element={<FeatureRoute feature="certificates" />}><Route path="certificates/:certificateId/preview" element={<CertificatePreview />} /></Route>
              <Route element={<FeatureRoute feature="certificates" />}><Route path="certificate-designer" element={<CertificateDesigner />} /></Route>
            </Route>
            <Route element={<AccessRoute area="settings" level="MANAGE" />}>
              <Route path="club-settings" element={<ClubSettings />} />
              <Route path="branding" element={<Branding />} />
            </Route>
          </Route>

          {/* Booking routes */}
          <Route path="booking">
            <Route index element={<BookingCalendar />} />
            <Route path="session/:instanceId" element={<SessionDetail />} />
            <Route path="checkout/:bookingId" element={<Checkout />} />
            <Route path="cart" element={<Cart />} />
            <Route path="cart-confirmation" element={<CartConfirmation />} />
            <Route path="cart-checkout" element={<Navigate to="/booking/cart" replace />} />
            <Route path="confirmation/:bookingId" element={<BookingConfirmation />} />
            <Route path="my-bookings" element={<MyBookings />} />
            {/* Waitlists are a section of My Bookings, charges of My Account */}
            <Route path="my-waitlist" element={<Navigate to="/booking/my-bookings" replace />} />
            <Route path="my-account" element={<MyChildren />} />
            <Route element={<FeatureRoute feature="notifications" to="/booking/my-account" />}><Route path="notifications" element={<NotificationsPage />} /></Route>

            {/* Admin pages, each for staff with access to its area (utils/access.js) */}
            <Route element={<AccessRoute area="sessions" level="VIEW" />}>
              <Route path="admin" element={<BookingAdmin />} />
            </Route>
            <Route element={<AccessRoute area="sessionSetup" level="MANAGE" />}>
              <Route path="admin/closures" element={<AdminClosures />} />
              <Route path="admin/session-management" element={<SessionManagement />} />
              <Route path="admin/session-types" element={<AdminSessionTypes />} />
              <Route element={<FeatureRoute feature="sessionCoaches" to="/booking/admin" />}><Route path="admin/coach-hours" element={<AdminCoachHours />} /></Route>
            </Route>
            <Route element={<AccessRoute area="registers" level="VIEW" />}>
              <Route path="admin/register" element={<NextRegister />} />
              <Route path="admin/register/:instanceId" element={<AdminRegister />} />
            </Route>
            <Route element={<AccessRoute area="competitions" level="VIEW" />}>
              <Route element={<FeatureRoute feature="competitions" />}><Route path="admin/competitions" element={<AdminCompetitions />} /></Route>
              <Route element={<FeatureRoute feature="competitions" />}><Route path="admin/competitions/:id" element={<AdminCompetitionDetail />} /></Route>
            </Route>
            <Route element={<AccessRoute area="firstAid" level="VIEW" />}>
              <Route path="admin/incidents" element={<AdminIncidents />} />
              <Route path="admin/incidents/new" element={<AdminIncidentForm />} />
              <Route path="admin/incidents/:id" element={<AdminIncidentDetail />} />
              <Route path="admin/incidents/:id/edit" element={<AdminIncidentForm />} />
            </Route>
            <Route element={<AccessRoute area="welfare" level="VIEW" />}>
              <Route path="admin/welfare" element={<AdminWelfare />} />
              <Route path="admin/welfare/new" element={<AdminWelfareForm />} />
              <Route path="admin/welfare/:id" element={<AdminWelfareDetail />} />
              <Route path="admin/welfare/:id/edit" element={<AdminWelfareForm />} />
            </Route>
            <Route element={<AccessRoute area="members" level="VIEW" />}>
              <Route path="admin/members" element={<AdminMembers />} />
              <Route path="admin/members/:userId" element={<AdminMemberProfile />} />
              <Route element={<FeatureRoute feature="bgNumbers" to="/booking/admin" />}><Route path="admin/bg-numbers" element={<AdminBgNumbers />} /></Route>
            </Route>
            <Route element={<AccessRoute area="payments" level="VIEW" />}>
              <Route path="admin/card-payments" element={<AdminCardPayments />} />
              <Route path="admin/payouts" element={<PayoutsRedirect />} />
              <Route path="admin/charges" element={<AdminCharges />} />
              <Route path="admin/credits" element={<AdminCredits />} />
              <Route element={<FeatureRoute feature="memberships" />}><Route path="admin/memberships" element={<AdminMemberships />} /></Route>
              <Route path="admin/payments" element={<AdminPaymentsPage />} />
              <Route path="admin/member-payments" element={<AdminMemberPayments />} />
            </Route>
            {/* In-app messaging for staff; Reports there are for club admins and welfare */}
            <Route element={<FeatureRoute feature="messaging" />}>
              <Route element={<AccessRoute anyOf={[['communications', 'VIEW'], ['welfare', 'MANAGE']]} />}>
                <Route path="admin/chat" element={<AdminChat />} />
              </Route>
              <Route element={<AccessRoute area="communications" level="VIEW" />}>
                <Route path="admin/chat/groups/:id" element={<ChatGroupForm />} />
              </Route>
              <Route element={<AccessRoute area="communications" level="MANAGE" />}>
                <Route path="admin/chat/groups/new" element={<ChatGroupForm />} />
              </Route>
            </Route>
            <Route element={<AccessRoute area="communications" level="MANAGE" />}>
              <Route path="admin/messages" element={<AdminMessages />} />
            </Route>
            <Route element={<FeatureRoute feature="messaging" />}>
              <Route element={<FeatureRoute feature="communityShop" />}>
                {/* As the server: Communications, or welfare (who can open its chats) */}
                <Route element={<AccessRoute anyOf={[['communications', 'VIEW'], ['welfare', 'VIEW']]} />}>
                  <Route path="admin/community" element={<AdminCommunityShop />} />
                </Route>
              </Route>
            </Route>
            {/* As the server: communications or managing members */}
            <Route element={<AccessRoute anyOf={[['communications', 'VIEW'], ['members', 'MANAGE']]} />}>
              <Route path="admin/recipient-groups" element={<AdminRecipientGroups />} />
            </Route>
            <Route element={<AccessRoute area="shop" level="VIEW" />}>
              <Route element={<FeatureRoute feature="shop" />}><Route path="admin/shop-orders" element={<AdminShopOrders />} /></Route>
            </Route>
            <Route element={<AccessRoute area="shop" level="MANAGE" />}>
              <Route element={<FeatureRoute feature="shop" />}><Route path="admin/shop-products" element={<AdminShopProducts />} /></Route>
              <Route element={<FeatureRoute feature="shop" />}><Route path="admin/shop-products/new" element={<AdminShopProduct />} /></Route>
              <Route element={<FeatureRoute feature="shop" />}><Route path="admin/shop-products/:id" element={<AdminShopProduct />} /></Route>
            </Route>
            <Route element={<AccessRoute area="business" level="VIEW" />}>
              <Route element={<FeatureRoute feature="invoices" />}><Route path="admin/invoices" element={<AdminInvoices />} /></Route>
              <Route element={<FeatureRoute feature="invoices" />}><Route path="admin/invoices/:id" element={<AdminInvoiceDetail />} /></Route>
              <Route element={<FeatureRoute feature="invoices" />}><Route path="admin/business-contacts" element={<AdminBusinessContacts />} /></Route>
            </Route>
            <Route element={<AccessRoute area="auditLog" level="VIEW" />}>
              <Route path="admin/audit-log" element={<AuditLog />} />
            </Route>
            {/* Roles and Recent updates are for Club admins only */}
            <Route element={<AccessRoute clubAdmin />}>
              <Route path="admin/roles" element={<AdminRoles />} />
              <Route element={<FeatureRoute feature="recentUpdates" />}><Route path="admin/changelog" element={<AdminChangelog />} /></Route>
            </Route>
            {/* For every member of staff: Help (the Help link takes staff here) */}
            <Route element={<AccessRoute staff />}>
              <Route path="admin/help" element={<AdminHelpPage />} />
            </Route>

            <Route element={<FeatureRoute feature="shop" />}><Route path="shop" element={<ShopListing />} /></Route>
            <Route path="shop/cart" element={<Navigate to="/booking/cart" replace />} />
            <Route element={<FeatureRoute feature="shop" />}><Route path="shop/:productId" element={<ShopProduct />} /></Route>
            <Route element={<FeatureRoute feature="shop" />}><Route path="shop/confirmation/:orderId" element={<ShopConfirmation />} /></Route>
            <Route element={<FeatureRoute feature="shop" />}><Route path="my-orders" element={<MyOrders />} /></Route>
            <Route path="my-charges" element={<Navigate to="/booking/my-account" replace />} />
            {/* Shared with members; staff controls inside follow access */}
            <Route path="noticeboard" element={<Noticeboard />} />
            <Route element={<FeatureRoute feature="messaging" />}>
              <Route path="messages" element={<Messages />} />
              <Route path="messages/:id" element={<Conversation />} />
              {/* Members' own kit; buyer and seller talk in Messages */}
              <Route element={<FeatureRoute feature="communityShop" />}>
                <Route path="community" element={<CommunityShop />} />
                <Route path="community/mine" element={<CommunityShop mine />} />
                <Route path="community/new" element={<CommunityListingForm />} />
                <Route path="community/:id" element={<CommunityListing />} />
                <Route path="community/:id/edit" element={<CommunityListingForm />} />
              </Route>
            </Route>
            <Route path="help" element={<HelpPage />} />
            <Route element={<FeatureRoute feature="competitions" />}><Route path="competitions" element={<MyCompetitions />} /></Route>
            <Route element={<FeatureRoute feature="requirements" />}><Route path="requirements" element={<Requirements />} /></Route>
            <Route element={<FeatureRoute feature="competitions" />}><Route path="competitions/:entryId/enter" element={<CompetitionEntry />} /></Route>
            <Route path="incidents" element={<MyIncidents />} />
            <Route path="incidents/:id" element={<MyIncidentDetail />} />
            <Route path="accept-invite/:token" element={<AcceptInvite />} />
          </Route>
        </Route>
      </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <RateLimitProvider>
          <AppContent />
        </RateLimitProvider>
      </AuthProvider>
    </BrandingProvider>
  );
}

export default App;
